/* Signals: world events proposing probability deltas — nothing moves
   until a human applies it (decision D8). */
import React, { useState } from 'react';
import { I } from '../ui.js';
import { useStore } from '../store.jsx';
import { api, type SignalView } from '../api.js';
import { Drawer } from './Drawers.jsx';

function ImpactChips({ signal }: { signal: SignalView }) {
  const { s } = useStore();
  return (
    <div className="sig-impacts">
      {signal.impacts.map((im, i) => {
        const scn = s.scenarios.find(x => x.id === im.scenarioId);
        return (
          <span key={i} className={`impact ${im.delta > 0 ? 'up' : 'down'}`}>
            <I k={im.delta > 0 ? 'arrowUp' : 'arrowDown'} /> {scn?.code ?? im.scenarioId} {im.delta > 0 ? '+' : ''}{im.delta}
          </span>
        );
      })}
    </div>
  );
}

function AddSignal() {
  const { s, toast } = useStore();
  const llm = s.capabilities?.features?.llm?.status === 'ga';
  const [headline, setHeadline] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualRows, setManualRows] = useState<{ scenarioId: string; delta: number }[]>([]);
  const modeled = s.scenarios.filter(x => x.defaultProb > 0);

  const analyze = async () => {
    if (headline.trim().length < 8) { toast('Write the headline first'); return; }
    setBusy(true);
    try {
      await api.analyzeSignal(headline.trim(), source.trim() || undefined);
      setHeadline(''); setSource('');
      toast('Signal analyzed — review the proposed impacts');
    } catch (err) { toast((err as Error).message); }
    setBusy(false);
  };

  const addManual = async () => {
    const impacts = manualRows.filter(r => r.delta !== 0);
    if (headline.trim().length < 8 || !impacts.length) { toast('Headline plus at least one non-zero impact'); return; }
    setBusy(true);
    try {
      await api.createSignal({ headline: headline.trim(), source: source.trim(), impacts });
      setHeadline(''); setSource(''); setManualRows([]);
      toast('Signal added as proposal');
    } catch (err) { toast((err as Error).message); }
    setBusy(false);
  };

  return (
    <>
      <div className="section-h">Add a signal</div>
      <div className="field"><input className="input sm" value={headline} placeholder="Headline — e.g. “Major aquifer depletion confirmed across grain belt”"
        onChange={e => setHeadline(e.target.value)} /></div>
      <div className="iv-custom-add">
        <input className="input sm" value={source} placeholder="Source (optional)" onChange={e => setSource(e.target.value)} />
        {llm
          ? <button className="btn sm primary" disabled={busy} onClick={() => void analyze()}><I k="wand" /> Analyze</button>
          : <button className="btn sm primary" disabled={busy} onClick={() => void addManual()}><I k="plus" /> Add</button>}
      </div>
      {!llm && (
        <>
          <div className="iv-add-lbl sub">impacts (no language model configured — set them manually)</div>
          {manualRows.map((row, i) => (
            <div key={i} className="iv-custom-add">
              <select className="select sm" value={row.scenarioId}
                onChange={e => setManualRows(rs => rs.map((r, j) => j === i ? { ...r, scenarioId: e.target.value } : r))}>
                {modeled.map(scn => <option key={scn.id} value={scn.id}>{scn.code} — {scn.name}</option>)}
              </select>
              <input className="input sm" type="number" min={-10} max={10} value={row.delta} style={{ width: 80, flex: 'none' }}
                onChange={e => setManualRows(rs => rs.map((r, j) => j === i ? { ...r, delta: Number(e.target.value) } : r))} />
              <button className="mini danger" onClick={() => setManualRows(rs => rs.filter((_, j) => j !== i))}><I k="trash" /></button>
            </div>
          ))}
          <button className="btn sm" onClick={() => setManualRows(rs => [...rs, { scenarioId: modeled[0]?.id ?? 'bau2', delta: 1 }])}>
            <I k="plus" /> Impact row
          </button>
        </>
      )}
    </>
  );
}

export function SignalsDrawer() {
  const { s, set, toast } = useStore();
  const proposed = s.signals.filter(x => x.status === 'proposed');
  const decided = s.signals.filter(x => x.status !== 'proposed');
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (id: string, action: 'apply' | 'dismiss') => {
    setBusy(id);
    try {
      if (action === 'apply') {
        const res = await api.applySignal(id);
        toast(res.applied.length
          ? `Applied — ${res.applied.map(a => `${a.scenarioId} ${a.from}→${a.to}`).join(', ')}`
          : 'Applied — no probabilities needed to move');
      } else {
        await api.dismissSignal(id);
      }
    } catch (err) { toast((err as Error).message); }
    setBusy(null);
  };

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<><I k="globe" /> Live geopolitical signals</>}
      title="Signals — probabilities never move silently"
      foot={<><div className="muted" style={{ fontSize: 11.5 }}>{proposed.length} proposal{proposed.length === 1 ? '' : 's'} awaiting your decision · every application is audited</div>
        <div className="grow" /><button className="btn primary" onClick={() => set({ drawer: null })}>Done</button></>}>
      <p className="prose" style={{ marginTop: -4 }}>
        Signals <strong>propose</strong> scenario-probability changes; you <strong>apply or dismiss</strong> them.
        Applying moves the live probabilities (clamped 1–99, obsolete runs never resurrect) with a full audit trail.
      </p>

      {proposed.length === 0 && <div className="callout info"><div className="ico"><I k="check" /></div><div>No open proposals — the matrix reflects every decision you've made.</div></div>}
      {proposed.map(sig => (
        <div className="signal" key={sig.id}>
          <div className="sig-date">{sig.label}</div>
          <div className="sig-main">
            <div className="sig-head">{sig.headline}</div>
            {sig.source && <div className="sig-src">{sig.source}</div>}
            <ImpactChips signal={sig} />
            {sig.rationale && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{sig.rationale}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
              <button className="btn sm primary" disabled={busy === sig.id} onClick={() => void act(sig.id, 'apply')}>
                <I k="check" /> Apply
              </button>
              <button className="btn sm" disabled={busy === sig.id} onClick={() => void act(sig.id, 'dismiss')}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}

      <AddSignal />

      {decided.length > 0 && (
        <>
          <div className="section-h">Decided</div>
          {decided.slice(0, 12).map(sig => (
            <div className="signal" key={sig.id} style={{ opacity: .62 }}>
              <div className="sig-date">{sig.label}</div>
              <div className="sig-main">
                <div className="sig-head">{sig.headline}</div>
                <div className="sig-src">{sig.status} by {sig.decidedBy ?? '—'}{sig.decidedAt ? ` · ${new Date(sig.decidedAt).toLocaleDateString()}` : ''}</div>
                <ImpactChips signal={sig} />
              </div>
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}
